import axios from 'axios'
import auth from '../auth/auth'
import saveAs from 'file-saver'

const a = axios.create({
    baseURL: process.env.VUE_APP_API_ROOT,
})

a.interceptors.request.use((config) => {
    const token = auth.getToken()

    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    } else {
        config.headers.Authorization = ''
    }
    return config
}, (err) => {
    return Promise.reject(err)
})

const urls = {
    findPrograms: '/programs/labels?term={term}',
    allRules: '/rules',
    getRule: '/rules/{id}?transactionType={transactionType}',
    updateRule: '/rules/{id}',
    ruleExport: '/rules/{id}/export/csv?transactionType={transactionType}',
    ruleImport: '/rules/{id}/import/csv?transactionType={transactionType}',
    ruleHistoryItems: '/ruleHistoryItem/{id}?transactionType={transactionType}',
    ruleHistoryItemsExport: '/ruleHistoryItem/{id}/export/{format}?transactionType={transactionType}',
    allRulesHistoryItemsExport: '/ruleHistoryItem/export/{format}',
    allRulesHistoryItems: '/ruleHistoryItem',
    detectionReport: '/detections/from/{fromTimestamp}/to/{toTimestamp}/pageNo/{pageNumber}',
    detectionReportExport: '/detections/export/{format}/from/{fromTimestamp}/to/{toTimestamp}',
    excludeFromRuleAndUnlock: '/detections/{detectionId}/excludeFromRuleAndUnlock/{type}',
    rangeTypes: '/rules/rangetypes',
    transactionTypes: '/rules/transactionTypes',
    inclusionTypes: '/rules/{transactionType}/inclusionTypes',
    exclusionTypes: '/rules/exclusionTypes',
    findParticipants: '/participants/labels?programId={programId}&term={term}',
}

export const api = {
    getConfig () {
        return axios.get('/config.json').then((response) => response.data)
    },
    getPrograms (term) {
        return new Promise((resolve, reject) => {
            a.get(urls.findPrograms.replace('{term}', term))
                .then(resp => {
                    resolve(resp.data)
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    getRule (ruleId, transactionType) {
        return new Promise((resolve, reject) => {
            a.get(urls.getRule.replace('{id}', ruleId).replace('{transactionType}', transactionType))
                .then(resp => {
                    resolve(convertRuleFromDTO(resp.data))
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    createRule (rule) {
        const ruleDTO = convertRuleToDTO(rule)
        return new Promise((resolve, reject) => {
            a.post(urls.allRules, ruleDTO)
                .then(resp => {
                    resolve(convertRuleFromDTO(resp.data))
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    updateRule (ruleId, rule) {
        const ruleDTO = convertRuleToDTO(rule)
        return new Promise((resolve, reject) => {
            a.put(urls.updateRule.replace('{id}', ruleId), ruleDTO)
                .then(resp => {
                    resolve(convertRuleFromDTO(resp.data))
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    exportRule (ruleId, transactionType) {
        return new Promise((resolve, reject) => {
            a.get(urls.ruleExport.replace('{id}', ruleId).replace('{transactionType}', transactionType), { responseType: 'blob' }).then((response) => {
                resolve(handleBlobResponse(response))
            }).catch(err => {
                reject(err.response)
            })
        })
    },
    importRule (ruleId, transactionType, formData) {
        return new Promise((resolve, reject) => {
            a.post(urls.ruleImport.replace('{id}', ruleId).replace('{transactionType}', transactionType), formData)
                .then(resp => {
                    resolve(convertRuleFromDTO(resp.data))
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    getAllRules (pageNumber, pageSize, sortBy, sortDesc) {
        return new Promise((resolve, reject) => {
            const sortOrder = sortDesc ? 'desc' : 'asc'
            const url = `${urls.allRules}?pageNo=${pageNumber}&pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}`
            a.get(url)
                .then(resp => {
                    resolve({
                        rules: resp.data.rules.map((ruleDTO) => convertRuleFromDTO(ruleDTO)),
                        totalRecordsCount: resp.data.totalRecordsCount,
                    })
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    excludeFromRuleAndUnlock (detectionId, type, description, unlock) {
        return new Promise((resolve, reject) => {
            a.post(urls.excludeFromRuleAndUnlock
                            .replace('{detectionId}', detectionId)
                            .replace('{type}', type) + (unlock ? '?unlock=true' : '?unlock=false'),
                { description: description })
                .then(resp => {
                    resolve(resp)
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    getRuleHistoryItems (ruleId, transactionType, pageNumber, pageSize, sortBy, sortDesc, programId, updatedBy) {
        return new Promise((resolve, reject) => {
            const sortOrder = sortDesc ? 'desc' : 'asc'
            let qs = `pageNo=${pageNumber}&pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}`
            if (programId) {
                qs += `&programId=${encodeURIComponent(programId)}`
            }
            if (updatedBy) {
                qs += `&updatedBy=${encodeURIComponent(updatedBy)}`
            }
            // Per-rule URL already carries ?transactionType=, so append paging with '&'; general uses '?'.
            const url = ruleId
            ? `${urls.ruleHistoryItems.replace('{id}', ruleId).replace('{transactionType}', transactionType)}&${qs}`
            : `${urls.allRulesHistoryItems}?${qs}`

            a.get(url).then(resp => {
                resolve({
                    ruleHistoryItems: resp.data.ruleHistoryItems.map((ruleDTO) => convertRuleFromDTO(ruleDTO)),
                    totalRecordsCount: resp.data.totalRecordsCount,
                })
            })
            .catch(err => {
                reject(err.response)
            })
        })
    },
    exportRuleHistoryItems (ruleId, transactionType, format) {
        return new Promise((resolve, reject) => {
              const url = ruleId
                  ? urls.ruleHistoryItemsExport.replace('{id}', ruleId).replace('{format}', format).replace('{transactionType}', transactionType)
                  : urls.allRulesHistoryItemsExport.replace('{format}', format)

              a.get(url, { responseType: 'blob' }).then((response) => {
                resolve(handleBlobResponse(response))
              }).catch(err => {
                reject(err.response)
              })
            })
    },
    getBlockedCards (fromTimestamp, toTimestamp, pageNumber, pageSize, sortBy, sortDesc, cardNumber, programCode) {
        return new Promise((resolve, reject) => {
            const sortOrder = sortDesc ? 'desc' : 'asc' // Convert boolean to 'ASC' or 'DESC'
            let url = `${urls.detectionReport.replace('{fromTimestamp}', fromTimestamp).replace('{toTimestamp}', toTimestamp).replace('{pageNumber}', pageNumber)}?pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}`
            if (cardNumber) {
                url += `&cardNumber=${encodeURIComponent(cardNumber)}`
            }
            if (programCode) {
                url += `&programCode=${encodeURIComponent(programCode)}`
            }

            a.get(url).then(resp => {
                    resolve(resp.data)
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    exportDetectionReport (fromTimestamp, toTimestamp, format, cardNumber, programCode) {
        return new Promise((resolve, reject) => {
            let url = urls.detectionReportExport
                .replace('{fromTimestamp}', fromTimestamp)
                .replace('{toTimestamp}', toTimestamp)
                .replace('{format}', format)
            const params = []
            if (cardNumber) {
                params.push(`cardNumber=${encodeURIComponent(cardNumber)}`)
            }
            if (programCode) {
                params.push(`programCode=${encodeURIComponent(programCode)}`)
            }
            if (params.length) {
                url += `?${params.join('&')}`
            }
            a.get(url, { responseType: 'blob' })
                .then((response) => {
                    resolve(handleBlobResponse(response))
                }).catch(err => {
                    reject(err.response)
                })
        })
    },
    getRangeTypes () {
        return new Promise((resolve, reject) => {
            a.get(urls.rangeTypes)
                .then(resp => {
                    resolve(resp.data)
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    getTransactionTypes () {
        return new Promise((resolve, reject) => {
            a.get(urls.transactionTypes)
                .then(resp => {
                    resolve(resp.data)
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    getInclusionTypes (transactionType) {
        return new Promise((resolve, reject) => {
            a.get(urls.inclusionTypes.replace('{transactionType}', transactionType))
                .then(resp => {
                    resolve(resp.data)
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    getExclusionTypes () {
        return new Promise((resolve, reject) => {
            a.get(urls.exclusionTypes)
                .then(resp => {
                    resolve(resp.data)
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
    getParticipants (programId, term) {
        return new Promise((resolve, reject) => {
            a.get(urls.findParticipants.replace('{term}', term).replace('{programId}', programId))
                .then(resp => {
                    resolve(resp.data)
                })
                .catch(err => {
                    reject(err.response)
                })
        })
    },
}

function handleBlobResponse (response) {
    const disposition = response.headers['content-disposition']
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = filenameRegex.exec(disposition)
    let filename = (matches != null && matches[1] ? matches[1] : 'export.csv')
    filename = filename.startsWith('UTF-8\'\'') ? filename.substring(7) : filename

    const blob = new Blob([response.data], { type: 'text/csv' })
    saveAs(blob, filename)

    return response
}

function convertRuleFromDTO (ruleDTO) {
    const ruleObject = ruleDTO
    ruleObject.timeRangeHours = ruleDTO.timeRange ? ruleDTO.timeRange / 60 : null // convert from minutes to hours
    return ruleObject
}

function convertRuleToDTO (ruleObject) {
    const ruleDTO = ruleObject
    ruleDTO.timeRange = ruleObject.timeRangeHours ? ruleObject.timeRangeHours * 60 : null // convert from hours to minutes
    // Send the CURRENT logged-in user so create/update records the actual editor (not the
    // round-tripped previous value). The backend prefers its JWT username and uses this as fallback.
    ruleDTO.updatedByUsername = auth.getUsername()
    return ruleDTO
}
